import React, { useState } from "react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Separator } from "./ui/separator";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  Building2,
  ShoppingCart,
  FileText,
  BarChart3,
  Users,
  Headphones,
  Download,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Check,
  X,
  Menu,
  Smartphone,
  MessageCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  XIcon,
} from "lucide-react";
import {
  companyInfo,
  carouselSlides,
  testimonials,
  institutionalText,
} from "../data/mock";
import { useNavigate } from "react-router-dom";

const LandingPage = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  const openWhatsApp = (isReseller = false) => {
    if (isReseller) {
      window.open(
        `https://api.whatsapp.com/send?phone=${companyInfo.resellerWhatsapp}&text=Olá, estou interessado em ser um revendedor Gateway.`,
        "_blank"
      );
    } else {
      window.open(
        `https://api.whatsapp.com/send?1=pt_BR&phone=${companyInfo.whatsapp}`,
        "_blank"
      );
    }
  };

  const businessSegments = [
    "Açougues",
    "Adegas",
    "Assistência técnica",
    "Atacados",
    "Autoelétricas",
    "Autopeças",
    "Bares",
    "Bazar",
    "Bicicletarias",
    "Bijuterias",
    "Borracharias",
    "Brinquedos",
    "Calçados",
    "Confecções",
    "Conveniências",
    "Distribuidoras",
    "Eletrodomésticos",
    "Esportes",
    "Ferragens",
    "Floriculturas",
    "Indústria",
    "Informática",
    "Joalherias",
    "Lanchonetes",
    "Livrarias",
    "Lubrificantes",
    "Manutenção",
    "Materiais de construção",
    "Materiais elétricos",
    "Mecânicas",
    "Mercados",
    "Móveis",
    "Oficinas",
    "Óticas",
    "Padarias",
    "Papelarias",
    "Perfumarias",
    "Pneus",
    "Presentes",
    "Prestadores de serviços",
    "Produtor rural",
    "Relojoarias",
    "Restaurantes",
    "Salões de beleza",
    "Som e imagem",
    "Supermercados",
    "Telefonia e celular",
    "Tintas",
    "Transportadoras",
    "Vendas externas",
    "Vidraçarias",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="img/gwayt.png"
                alt="Gateway Sistemas"
                className="h-6 w-auto sm:h-8 md:h-10 object-contain"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-4">
                <button
                  onClick={() => scrollToSection("home")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Home
                </button>
                <button
                  onClick={() => scrollToSection("institucional")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Institucional
                </button>
                <button
                  onClick={() => scrollToSection("solucoes")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Soluções
                </button>
                <button
                  onClick={() => scrollToSection("certificado")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Certificado Digital
                </button>
                <button
                  onClick={() => scrollToSection("suporte")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Suporte
                </button>
                <button
                  onClick={() => scrollToSection("revenda")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Revenda
                </button>
                <button
                  onClick={() => navigate("/ajuda")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Ajuda
                </button>
                <button
                  onClick={() => scrollToSection("contato")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Contato
                </button>
                <button
                  onClick={() => navigate("/xml")}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  XML
                </button>
                <a
                  href="https://gatewaysistemas.com.br/gway/#/gwaysistemas/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                >
                  Acesso ao Sistema
                </a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg rounded-b-lg">
                <button
                  onClick={() => {
                    scrollToSection("home");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Home
                </button>
                <button
                  onClick={() => {
                    scrollToSection("institucional");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Institucional
                </button>
                <button
                  onClick={() => {
                    scrollToSection("solucoes");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Soluções
                </button>
                <button
                  onClick={() => {
                    scrollToSection("suporte");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Suporte
                </button>
                <button
                  onClick={() => {
                    scrollToSection("revenda");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Revenda
                </button>
                <button
                  onClick={() => {
                    navigate("/ajuda");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Ajuda
                </button>
                <button
                  onClick={() => {
                    scrollToSection("contato");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  Contato
                </button>
                <button
                  onClick={() => {
                    navigate("/xml");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-base font-medium transition-colors"
                >
                  XML
                </button>
                <a
                  href="https://gatewaysistemas.com.br/gway/#/gwaysistemas/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-left bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-md text-base font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 mt-2"
                >
                  Acesso ao Sistema
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Carousel Section */}
      <section id="home" className="relative min-h-screen">
        <Carousel
          className="w-full h-screen"
          plugins={[
            Autoplay({
              delay: 5000,
            }),
          ]}
        >
          <CarouselContent>
            {carouselSlides.map((slide) => (
              <CarouselItem key={slide.id}>
                <div className="relative h-screen">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40 flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                      <div className="text-white max-w-3xl">
                        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                          {slide.title}
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
                          {slide.description}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <Button
                            onClick={() => {
                              if (slide.ctaAction === "reseller") {
                                openWhatsApp(true);
                              } else if (slide.ctaAction === "solutions") {
                                scrollToSection("solucoes");
                              }
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105"
                          >
                            {slide.ctaText}
                          </Button>
                          <Button
                            onClick={() => openWhatsApp()}
                            variant="outline"
                            className="border-white text-white hover:bg-white hover:text-black font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105"
                          >
                            <MessageCircle className="mr-2 h-5 w-5" />
                            Fale Conosco
                          </Button>
                        </div>

                        {/* Company Info Badge */}
                        <div className="mt-8 inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                          <span className="text-yellow-300 font-semibold mr-2">
                            Gway Sistemas
                          </span>                          
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4 bg-white/20 border-white/30 hover:bg-white/30" />
          <CarouselNext className="right-4 bg-white/20 border-white/30 hover:bg-white/30" />
        </Carousel>
      </section>

      {/* Institucional Section */}
      <section id="institucional" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              A Gateway
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {institutionalText.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-lg text-gray-700 mb-6 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}

              <div className="grid grid-cols-2 gap-8 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {companyInfo.yearsOfExperience}+
                  </div>
                  <div className="text-gray-600">Anos de Experiência</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {companyInfo.foundedYear}
                  </div>
                  <div className="text-gray-600">Fundada em</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg p-8 text-white">
                <Building2 className="h-16 w-16 mb-6 text-yellow-300" />
                <h3 className="text-2xl font-bold mb-4">Nossa Missão</h3>
                <p className="text-lg leading-relaxed">
                  {institutionalText.mission}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Soluções Section */}
      <section id="solucoes" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nossas Soluções
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sistemas completos de gestão empresarial adaptados para diferentes
              segmentos de negócio
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <Tabs defaultValue="integracoes" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="segmentos">Segmentos Atendidos</TabsTrigger>
              <TabsTrigger value="funcionalidades">Funcionalidades</TabsTrigger>
              <TabsTrigger value="integracoes">Integrações</TabsTrigger>
            </TabsList>

            <TabsContent value="segmentos" className="mt-8">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">
                  Segmentos de Atuação Gateway
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {businessSegments.map((segment, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="text-sm font-medium text-gray-800 text-center">
                        {segment}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="funcionalidades" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-2xl">
                      <FileText className="mr-3 h-8 w-8" />
                      Documentos Fiscais
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      <li>• NF-e (Nota Fiscal Eletrônica)</li>
                      <li>• NFC-e (Nota Fiscal do Consumidor Eletrônica)</li>
                      <li>• CT-e (Conhecimento de Transporte Eletrônico)</li>
                      <li>• MDF-e (Manifesto de Documentos Fiscais)</li>
                      <li>• NFS-e (Nota Fiscal de Serviços)</li>
                      <li>• SPED Fiscal e Contribuições</li>
                      <li>• Sintegra</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-2xl text-gray-900">
                      <BarChart3 className="mr-3 h-8 w-8 text-blue-600" />
                      Gestão Empresarial
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Contas a Pagar e Receber</li>
                      <li>• Fluxo de Caixa</li>
                      <li>• Controle de Estoque</li>
                      <li>• Pedidos de Venda e Compra</li>
                      <li>• Relatórios Gerenciais</li>
                      <li>• Backup Automático</li>
                      <li>• Integração entre Filiais</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="integracoes" className="mt-8">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">
                  Integrações Disponíveis
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* Restaurante */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">
                        Restaurante
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• iFood</li>
                        <li>• Neemo</li>
                        <li>• B2Food</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Lojas Virtuais */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">
                        Lojas Virtuais
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• WooCommerce</li>
                        <li>• Magento</li>
                        <li>• Loja Integrada</li>
                        <li>• Bling</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* TEF */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">TEF</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Fiserv</li>
                        <li>• SKYTEF</li>
                        <li>• GETCARD</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Promoções */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">Promoções</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Scanntech</li>
                        <li>• Nummus</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* PIX */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">PIX</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• OpenPix</li>
                        <li>• Fiserv</li>
                      </ul>
                    </CardContent>
                  </Card>
                  {/* Revisão Tributária */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-gray-900">
                        Revisão Tributária
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-gray-700">
                        <li>• iMendes</li>
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Boleto Bancário */}
                  <Card className="md:col-span-2 lg:col-span-3">
                    <CardHeader>
                      <CardTitle className="text-gray-900">
                        Boleto Bancário
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-gray-700">
                        <div>• Santander</div>
                        <div>• Caixa</div>
                        <div>• Sicoob</div>
                        <div>• Bradesco</div>
                        <div>• CredSIS</div>
                        <div>• Safra</div>
                        <div>• BRB</div>
                        <div>• Sicredi</div>
                        <div>• Itaú</div>
                        <div>• Banco do Brasil</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              O que nossos clientes dizem
            </h2>
            <p className="text-xl text-gray-600">
              Depoimentos reais de empresários que confiam na Gateway
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover-card h-full">
                <CardContent className="p-6 h-full flex flex-col">
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4 object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {testimonial.business}
                      </p>
                    </div>
                  </div>

                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, index) => (
                      <Star
                        key={index}
                        className="h-4 w-4 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>

                  <p className="text-gray-700 flex-grow leading-relaxed">
                    "{testimonial.content}"
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* Certificado Digital Section */}
      <section id="certificado" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Certificado Digital A1
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Garanta a validade jurídica dos seus documentos fiscais e emita
              NF-e, NFC-e e outros documentos com segurança.
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="hover-card text-center bg-gradient-to-br from-blue-50 to-purple-50">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                  Emissão de Certificado Digital A1
                </h3>
                <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                  O Certificado Digital A1 é essencial para emissão de notas
                  fiscais e autenticação segura de documentos eletrônicos. Fale
                  conosco para adquirir o seu e realizar a emissão 100% online.
                </p>
                <Button
                  onClick={() => openWhatsApp()}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Falar no WhatsApp
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Suporte Section */}
      <section id="suporte" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Suporte Técnico
            </h2>
            <p className="text-xl text-gray-600">
              Ferramentas de suporte remoto para atendimento rápido e eficiente
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Card className="hover-card">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl">gDesk</CardTitle>
                <CardDescription>Acesso remoto rápido e seguro</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600 mb-6">
                  Download da ferramenta gDesk para suporte técnico remoto
                  imediato.
                </p>
                <a
                  href="https://gatewaysistemas.com.br/Gateway/downloads/gDesk.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-purple-600 hover:bg-purple-700">
                    <Download className="mr-2 h-4 w-4" />
                    Download gDesk
                  </Button>
                </a>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">TeamViewer</CardTitle>
                <CardDescription>Acesso remoto rápido e seguro</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600 mb-6">
                  Download da ferramenta TeamViewer para suporte técnico remoto
                  imediato.
                </p>
                <a
                  href="https://gatewaysistemas.com.br/Gateway/downloads/TeamViewerQS_pt-idcwadwrwr.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    <Download className="mr-2 h-4 w-4" />
                    Download TeamViewer
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="hover-card">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">AnyDesk</CardTitle>
                <CardDescription>Alternativa de suporte remoto</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600 mb-6">
                  Download da ferramenta AnyDesk como opção alternativa para
                  suporte técnico.
                </p>
                <a
                  href="https://gatewaysistemas.com.br/Gateway/downloads/AnyDesk.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    className="w-full border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download AnyDesk
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Card className="max-w-2xl mx-auto bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="p-8">
                <Phone className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-4">Precisa de Ajuda?</h3>
                <p className="text-gray-600 mb-6">
                  Nossa equipe de suporte está pronta para ajudar você. Entre em
                  contato via WhatsApp ou telefone.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => openWhatsApp()}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp: {companyInfo.phone}
                  </Button>
                  <Button variant="outline">
                    <Phone className="mr-2 h-4 w-4" />
                    Telefone: {companyInfo.landlinePhone}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Revenda Section */}
      <section id="revenda" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Programa de Revenda
            </h2>
            <p className="text-xl text-gray-600">
              Torne-se um parceiro Gateway e expanda seus negócios
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="hover-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Suporte Completo</h3>
                <p className="text-gray-600">
                  Treinamento, materiais de marketing e suporte técnico
                  especializado para nossos revendedores.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">
                  Potencial de Lucratividade
                </h3>
                <p className="text-gray-600">
                  Defina seus próprios preços na sua região. Você paga um valor
                  fixo por licença e aplica a margem desejada.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Território Exclusivo</h3>
                <p className="text-gray-600">
                  Protegemos seu investimento com territórios exclusivos para
                  revendedores cadastrados.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-white p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">
              Interessado em se tornar um revendedor?
            </h3>
            <p className="text-xl mb-6 opacity-90">
              Entre em contato conosco e descubra como começar a vender nossas
              soluções.
            </p>
            <Button
              onClick={() => openWhatsApp(true)}
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
            >
              Quero ser Revendedor
            </Button>
          </div>
        </div>
      </section>

      {/* Ajuda Section */}
      <section id="ajuda" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Central de Ajuda Completa
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Acesse nossa central completa com manuais, tutoriais e
              documentação detalhada
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-8"></div>
            <Button
              onClick={() => navigate("/ajuda")}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-4 text-lg"
            >
              Acessar Central de Ajuda Completa
            </Button>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem
                value="item-1"
                className="bg-white rounded-lg shadow-sm"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="text-lg font-medium">
                    Como faço para emitir uma NF-e?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600">
                    Para emitir uma NF-e, acesse o módulo de vendas, selecione o
                    cliente, adicione os produtos ou serviços, configure os
                    dados fiscais e clique em "Transmitir NF-e". O sistema fará
                    toda a comunicação com a SEFAZ automaticamente.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-2"
                className="bg-white rounded-lg shadow-sm"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="text-lg font-medium">
                    Posso usar o sistema em múltiplas filiais?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600">
                    Sim! Os planos Gestor Pro e Indústria incluem integração
                    entre filiais, permitindo controle centralizado de estoque,
                    vendas e relatórios consolidados de todas as unidades.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-3"
                className="bg-white rounded-lg shadow-sm"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="text-lg font-medium">
                    Como funciona o backup automático?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600">
                    O sistema realiza backup automático diário dos seus dados,
                    armazenando-os de forma segura. Você pode configurar
                    horários personalizados e também fazer backups manuais
                    quando necessário.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-4"
                className="bg-white rounded-lg shadow-sm"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="text-lg font-medium">
                    O sistema funciona offline?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600">
                    Sim, oferecemos tanto a versão desktop (que funciona
                    offline) quanto a versão web. A versão desktop permite
                    trabalhar sem internet, sincronizando os dados quando a
                    conexão for restabelecida.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="item-5"
                className="bg-white rounded-lg shadow-sm"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="text-lg font-medium">
                    Qual a diferença entre os planos?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600">
                    O Emissor é focado apenas em documentos fiscais. O Gestor Pro inclui gestão
                    completa com apps móveis e PDV. O Indústria tem controles
                    específicos para indústrias.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contato Section */}
      <section id="contato" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fale Conosco
            </h2>
            <p className="text-xl text-gray-600">
              Entre em contato e tire suas dúvidas com nossa equipe
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="hover-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">WhatsApp</h3>
                <p className="text-gray-600 mb-6">
                  Fale diretamente conosco via WhatsApp para atendimento rápido
                  e personalizado.
                </p>
                <Button
                  onClick={() => openWhatsApp()}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {companyInfo.phone}
                </Button>
              </CardContent>
            </Card>

            <Card className="hover-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-4">Telefone Comercial</h3>
                <p className="text-gray-600 mb-6">
                  Ligue para nossa central de atendimento em horário comercial.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  {companyInfo.landlinePhone}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Card className="max-w-5xl mx-auto bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <MapPin className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-4">
                      Nossa Localização
                    </h3>
                    <div className="text-left space-y-2">
                      <p className="text-gray-600">
                        <strong>Gway Sistemas</strong>
                      </p>
                      <p className="text-gray-600">
                        {companyInfo.address.street}
                      </p>
                      <p className="text-gray-600">
                        {companyInfo.address.city}, {companyInfo.address.state}
                      </p>
                      <p className="text-gray-600">
                        CEP: {companyInfo.address.zipCode}
                      </p>
                      <p className="text-gray-600 font-medium mt-4">
                        Atendendo todo o Brasil há mais de{" "}
                        {companyInfo.yearsOfExperience} anos
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg overflow-hidden shadow-lg">
                    <iframe
                      src={companyInfo.address.mapUrl}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Gateway Sistemas - Localização"
                    ></iframe>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => openWhatsApp()}
          className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse hover:animate-none"
          size="icon"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_eda8ca4d-c957-4325-a9ab-0a26f5df1d41/artifacts/2jmiflv8_Gateway_500_200_2_C.png"
                alt="Gateway Sistemas"
                className="h-8 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                {companyInfo.yearsOfExperience} anos oferecendo soluções ERP
                completas para pequenos e micro empreendedores em todo o Brasil.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Soluções</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>G-way Emissor</li>
                <li>G-way Gestor Pro</li>
                <li>G-way Indústria</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button
                    onClick={() => navigate("/xml")}
                    className="hover:text-white transition-colors text-left"
                  >
                    Download XML
                  </button>
                </li>
                <li>
                  <a
                    href="https://gatewaysistemas.com.br/gway/#/gwaysistemas/"
                    className="hover:text-white transition-colors"
                  >
                    Acesso ao Sistema
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/ajuda")}
                    className="hover:text-white transition-colors text-left"
                  >
                    Central de Ajuda
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("revenda")}
                    className="hover:text-white transition-colors"
                  >
                    Seja Revendedor
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/termouso")}
                    className="hover:text-white transition-colors"
                  >
                    Termos de Uso
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  {companyInfo.landlinePhone}
                </li>
                <li className="flex items-center">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp: {companyInfo.phone}
                </li>
                <li className="flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  gatewaysistemas.com.br
                </li>
              </ul>
            </div>
          </div>

          <Separator className="my-8 bg-gray-700" />

          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2024 Gateway Sistemas. Todos os direitos reservados.</p>
            <p>
              Fundada em {companyInfo.foundedYear} •{" "}
              {companyInfo.yearsOfExperience}+ anos de experiência
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
